// AB#2558 — recovering a browser tab that is running a build the server no longer has.
//
// Every SPA here is code-split, and every deploy replaces /assets wholesale with freshly hashed
// filenames. A tab held open across a release therefore holds a module graph whose chunk URLs have
// been deleted: the next lazy import (a route split, an address map, anything) asks for
// `assets/Thing-OLDHASH.js`, gets a hard 404, and the app dies with "Failed to fetch dynamically
// imported module". Nothing the user can do inside the page fixes it — the boundary's "Try again"
// re-runs the same import against the same dead URL — because the fix is to fetch index.html again
// and pick up the new module graph.
//
// So: recognise that specific failure and reload once. The one-shot is what keeps this safe. A
// chunk that is genuinely broken (rather than merely stale) fails again immediately after the
// reload, and a naive "reload on chunk error" would spin forever; here the second failure inside
// the guard window is refused, and the host shows an explicit "reload" affordance instead.
//
// Framework-agnostic on purpose — the window listener alone gives the shells with no error boundary
// (central, process-designer, release) the same recovery the tenant gets through its boundary.

/** sessionStorage key holding the timestamp of the last automatic reload. */
const RELOAD_KEY = "marque:stale-build-reload-at";

/**
 * How long after an automatic reload a second stale-build failure is treated as "the chunk is
 * really broken" rather than "the build moved again". Comfortably longer than the reload +
 * re-navigate + re-import cycle that a loop would take, and far shorter than the gap between two
 * genuine deploys, so a tab open all day still self-heals on the second release.
 */
const DEFAULT_GUARD_MS = 30_000;

/**
 * Messages browsers use for a dynamic import that could not be fetched or parsed. Chromium and
 * Firefox word it differently, Safari differently again, and a host that answers missing assets
 * with the SPA index.html (rather than a 404) produces the MIME-type variant instead.
 */
const STALE_BUILD_PATTERNS: readonly RegExp[] = [
    /failed to fetch dynamically imported module/i,
    /error loading dynamically imported module/i,
    /importing a module script failed/i,
    /failed to load module script/i,
    /expected a javascript(?:-or-wasm)? module script/i,
    /is not a valid javascript mime type/i,
    /unable to preload css/i,
];

/** Seams, so the behaviour is testable without a real window. */
export interface StaleBuildEnv {
    /** Defaults to sessionStorage when there is one. */
    storage?: Pick<Storage, "getItem" | "setItem">;
    /** Defaults to a full page reload. */
    reload?: () => void;
    /** Defaults to Date.now. */
    now?: () => number;
    /** How long to refuse a second automatic reload for. Defaults to 30s. */
    guardMs?: number;
}

function defaultStorage(): Pick<Storage, "getItem" | "setItem"> | undefined {
    try {
        return typeof sessionStorage === "undefined" ? undefined : sessionStorage;
    } catch {
        // Safari private browsing and some embedded webviews throw on access rather than returning
        // undefined. No storage means no loop guard, so recovery declines to reload at all.
        return undefined;
    }
}

/**
 * True when this error is the browser failing to load a dynamically imported chunk — i.e. almost
 * always a stale build rather than a fault in the application code.
 */
export function isStaleBuildError(error: unknown): boolean {
    const message =
        error instanceof Error ? error.message
        : typeof error === "string" ? error
        : typeof (error as { message?: unknown } | null)?.message === "string" ? String((error as { message: string }).message)
        : "";
    if (message === "") { return false; }
    return STALE_BUILD_PATTERNS.some(p => p.test(message));
}

/**
 * Reload the page to pick up the current build — at most once per guard window.
 *
 * Returns true when a reload was started (the caller's page is going away and should do nothing
 * else), false when it was refused. A refusal means either there is no storage to guard with or a
 * reload has already been tried and did not help; the caller should surface the "a new version was
 * released" affordance rather than trying again.
 */
export function attemptStaleBuildReload(env: StaleBuildEnv = {}): boolean {
    const storage = env.storage ?? defaultStorage();
    if (!storage) { return false; }

    const now = (env.now ?? Date.now)();
    const guardMs = env.guardMs ?? DEFAULT_GUARD_MS;

    try {
        const previous = Number(storage.getItem(RELOAD_KEY));
        if (Number.isFinite(previous) && previous > 0 && now - previous < guardMs) { return false; }
        storage.setItem(RELOAD_KEY, String(now));
    } catch {
        return false;
    }

    const reload = env.reload ?? (() => { window.location.reload(); });
    reload();
    return true;
}

/**
 * Handle an error that may be a stale build: reloads (returning true) only when the error is a
 * chunk-load failure AND the guard window allows it.
 */
export function recoverFromStaleBuild(error: unknown, env: StaleBuildEnv = {}): boolean {
    return isStaleBuildError(error) && attemptStaleBuildReload(env);
}

/** What {@link installStaleBuildRecovery} listens on. Defaults to window. */
export interface StaleBuildInstallEnv extends StaleBuildEnv {
    target?: Pick<EventTarget, "addEventListener" | "removeEventListener">;
}

/**
 * Listen for stale-build failures at the window and reload once when one happens.
 *
 * Covers two arrivals of the same fault: Vite's own `vite:preloadError` (fired before it throws,
 * and cancellable), and an unhandled rejection from a dynamic import nothing caught. Anything that
 * *is* caught — React.lazy under an error boundary — reaches the host's boundary instead, which
 * should call {@link recoverFromStaleBuild} itself.
 *
 * Returns a teardown function.
 */
export function installStaleBuildRecovery(env: StaleBuildInstallEnv = {}): () => void {
    const target = env.target ?? (typeof window === "undefined" ? undefined : window);
    if (!target) { return () => {}; }

    const onPreloadError = (event: Event): void => {
        // Only swallow Vite's default throw if we are actually reloading; otherwise let it surface
        // so the host's boundary can offer the manual reload.
        if (attemptStaleBuildReload(env)) { event.preventDefault(); }
    };
    const onRejection = (event: Event): void => {
        recoverFromStaleBuild((event as PromiseRejectionEvent).reason, env);
    };

    target.addEventListener("vite:preloadError", onPreloadError);
    target.addEventListener("unhandledrejection", onRejection);
    return () => {
        target.removeEventListener("vite:preloadError", onPreloadError);
        target.removeEventListener("unhandledrejection", onRejection);
    };
}
